import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactECharts from "echarts-for-react";
import { Settings } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import DateScopeControl from "../OverdueValueTimeline/DateScopeControl";
import { useAbsoluteDelayTimeline } from "./hooks/useAbsoluteDelayTimeline";
import { useObjectTypeActivities } from "../LiefertermintreueKPI/hooks/useObjectTypeActivities";
import { useObjectDateAttributes } from "../LiefertermintreueKPI/hooks/useObjectDateAttributes";
import AbsoluteDelayTimelineSettings from "./AbsoluteDelayTimelineSettings";
import { ABSOLUTE_DELAY_TIMELINE_INFO_TEXT } from "./constants";
import {
  formatXLabel,
  sampleSnapshotByGranularity,
  deriveDailyDelayStats,
  computeRollingDelayStats,
  classifyThresholdValue,
  ZONE_COLORS,
} from "../_shared/deliveryTimelineUtils";

const fmtDays = (n) => (n == null ? "—" : `${Number(n).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Tage`);

function fmtGroupBreakdown(label, group) {
  if (!group || group.count === 0) return `0 ${label}`;
  const avg = group.sum / group.count;
  const maxPart = group.max != null && group.max > avg ? `, Höchstwert ${fmtDays(group.max)}` : "";
  return `${group.count} ${label} (Ø ${fmtDays(avg)}${maxPart})`;
}

const AGGREGATION_LABELS = { min: "Minimum", median: "Median", average: "Durchschnitt", max: "Maximum", sum: "Summe" };

const DEFAULT_TITLE = "Absoluter Verzug über Zeit";

/** Small circle-i button; opens the project's white-card Radix tooltip on hover/focus. */
const InfoIcon = ({ label, description }) => (
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <button
        type="button"
        className="inline-flex text-gray-400 hover:text-gray-500 transition-colors align-middle"
        aria-label={`${label} info`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        side="top"
        align="center"
        sideOffset={6}
        className="max-w-[280px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
      >
        <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
          <span className="text-xs font-medium text-primary-700">{label}</span>
        </div>
        <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
          {description}
        </div>
        <Tooltip.Arrow className="fill-white" />
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);

const AbsoluteDelayTimeline = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    componentTitle, setComponentTitle,
    objectType, setObjectType,
    refDateType, setRefDateType,
    refDateValue, setRefDateValue,
    refEventAttributeSource, setRefEventAttributeSource,
    actualDateType, setActualDateType,
    actualDateValue, setActualDateValue,
    actualEventAttributeSources, setActualEventAttributeSources,
    tileEventAttributeFilter, setTileEventAttributeFilter,
    objectTypes,
    metadata,
    localFilters, updateLocalFilter, clearLocalFilters, hasLocalFilters, localFilterCount,
    startDraftMode, applyDraftFilters, cancelDraftFilters,
    viewMode, setViewMode,
    granularity, setGranularity,
    dateRangeStart, setDateRangeStart,
    dateRangeEnd, setDateRangeEnd,
    aggregation, setAggregation,
    includeMode, setIncludeMode,
    rollingWindowDays, setRollingWindowDays,
    thresholdType, setThresholdType,
    thresholdTarget, setThresholdTarget,
    thresholdWarningPercentage, setThresholdWarningPercentage,
    thresholdLower, setThresholdLower,
    thresholdUpper, setThresholdUpper,
    chartData,
    loading,
    error,
    hasConfig,
    fetchData,
  } = useAbsoluteDelayTimeline(activeFile, initialSettings);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // ── Settings-modal draft object type (see LiefertermintreueTimeline.jsx for rationale) ──
  const [settingsObjectType, setSettingsObjectType] = useState(objectType);
  useEffect(() => {
    if (isSettingsModalOpen) setSettingsObjectType(objectType);
  }, [isSettingsModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const { activities: activitiesForType, loading: activitiesLoading } = useObjectTypeActivities(activeFile, settingsObjectType);
  const { dateAttributes: dateAttrsForType, loading: dateAttrsLoading } = useObjectDateAttributes(activeFile, settingsObjectType);

  // Activity -> available event attribute names, from the already-loaded OCEL
  // metadata (metadata.event_types), used by the "Event-Attribut" date source.
  const getAttributesForActivity = useCallback(
    (activity) => metadata?.event_types?.find((t) => t.name === activity)?.attributes?.map((a) => a.name) || [],
    [metadata]
  );

  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.refDateType !== undefined) setRefDateType(initialSettings.refDateType);
    if (initialSettings.refDateValue !== undefined) setRefDateValue(initialSettings.refDateValue);
    if (initialSettings.refEventAttributeSource !== undefined) setRefEventAttributeSource(initialSettings.refEventAttributeSource);
    if (initialSettings.actualDateType !== undefined) setActualDateType(initialSettings.actualDateType);
    if (initialSettings.actualDateValue !== undefined) setActualDateValue(initialSettings.actualDateValue);
    if (initialSettings.actualEventAttributeSources !== undefined) setActualEventAttributeSources(initialSettings.actualEventAttributeSources);
    if (initialSettings.tileEventAttributeFilter !== undefined) setTileEventAttributeFilter(initialSettings.tileEventAttributeFilter);
    if (initialSettings.aggregation !== undefined) setAggregation(initialSettings.aggregation);
    if (initialSettings.includeMode !== undefined) setIncludeMode(initialSettings.includeMode);
    if (initialSettings.rollingWindowDays !== undefined) setRollingWindowDays(initialSettings.rollingWindowDays);
    if (initialSettings.thresholdType !== undefined) setThresholdType(initialSettings.thresholdType);
    if (initialSettings.thresholdTarget !== undefined) setThresholdTarget(initialSettings.thresholdTarget);
    if (initialSettings.thresholdWarningPercentage !== undefined) setThresholdWarningPercentage(initialSettings.thresholdWarningPercentage);
    if (initialSettings.thresholdLower !== undefined) setThresholdLower(initialSettings.thresholdLower);
    if (initialSettings.thresholdUpper !== undefined) setThresholdUpper(initialSettings.thresholdUpper);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onTitleChange?.(componentTitle || DEFAULT_TITLE);
  }, [componentTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      componentTitle, objectType, refDateType, refDateValue, refEventAttributeSource,
      actualDateType, actualDateValue, actualEventAttributeSources,
      tileEventAttributeFilter, aggregation, includeMode, rollingWindowDays,
      thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper,
      localFilters,
    }));
  }, [componentTitle, objectType, refDateType, refDateValue, refEventAttributeSource,
      actualDateType, actualDateValue, actualEventAttributeSources,
      tileEventAttributeFilter, aggregation, includeMode, rollingWindowDays,
      thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper,
      localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onRefresh?.(() => { if (hasConfig) fetchData(); });
  }, [onRefresh, hasConfig, fetchData]);

  useEffect(() => {
    if (hasConfig) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConfig, objectType, refDateType, refDateValue, refEventAttributeSource,
      actualDateType, actualDateValue, actualEventAttributeSources, tileEventAttributeFilter,
      rollingWindowDays]);

  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {hasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={hasLocalFilters}
            localFilterCount={localFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
        >
          <Settings className="w-3 h-3" />
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters]
  );

  useEffect(() => {
    onGetHeaderActions?.(headerActions);
  }, [onGetHeaderActions, headerActions]);

  const filteredDataPoints = useMemo(() => {
    const points = chartData?.data_points ?? [];
    if (viewMode !== "daterange" || !dateRangeStart || !dateRangeEnd) return points;
    return points.filter((pt) => pt.date >= dateRangeStart && pt.date <= dateRangeEnd);
  }, [chartData, viewMode, dateRangeStart, dateRangeEnd]);

  const population = includeMode === "all" ? "all" : "late_only";

  // ── Single-day ("Roh") derivation: merges that day's `open` group with the
  // completed cohort for `population`, plus the backend's exact single-day
  // median. Must run before granularity resampling. ───────────────────────────
  const derivedDaily = useMemo(
    () => deriveDailyDelayStats(filteredDataPoints, population),
    [filteredDataPoints, population]
  );

  // ── Granularity resampling: min/median/average/max are not additive across days,
  // so coarser granularities take a point-in-time snapshot (last day of the bucket)
  // instead of pooling/averaging multiple days. ─────────────────────────────────
  const sampledPoints = useMemo(
    () => sampleSnapshotByGranularity(derivedDaily, granularity),
    [derivedDaily, granularity]
  );

  // ── Rolling-window smoothing overlay: exact for average/min/max/sum (see
  // computeRollingDelayStats), deliberately NOT offered for median. ───────────
  const showSmoothing = aggregation !== "median";
  // Rolling average is computed over lookback + full data (so the first
  // displayed day already has a full trailing window, see deliveryTimelineUtils.js),
  // then sliced back to the lookback length and, in "Zeitraum" mode, filtered to
  // the visible range — never over the pre-truncated filteredDataPoints.
  const rollingDaily = useMemo(() => {
    if (!showSmoothing) return [];
    const lookback = chartData?.lookback_data_points ?? [];
    const full = chartData?.data_points ?? [];
    const rolledFull = computeRollingDelayStats([...lookback, ...full], rollingWindowDays, population).slice(lookback.length);
    if (viewMode !== "daterange" || !dateRangeStart || !dateRangeEnd) return rolledFull;
    return rolledFull.filter((pt) => pt.date >= dateRangeStart && pt.date <= dateRangeEnd);
  }, [showSmoothing, chartData, rollingWindowDays, population, viewMode, dateRangeStart, dateRangeEnd]);
  const rollingSampled = useMemo(
    () => (showSmoothing ? sampleSnapshotByGranularity(rollingDaily, granularity) : []),
    [showSmoothing, rollingDaily, granularity]
  );

  const thresholdConfig = useMemo(
    () => ({ thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper }),
    [thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper]
  );

  const echartsOption = useMemo(() => {
    if (!sampledPoints?.length) return null;

    const dates = sampledPoints.map((p) => p.date);
    const rawValues = sampledPoints.map((p) => p[aggregation] ?? null);
    const smoothedValues = showSmoothing ? rollingSampled.map((p) => p[aggregation]) : [];
    const lateCounts = sampledPoints.map((p) => p.lateCount);
    const eligibleCounts = sampledPoints.map((p) => p.evaluatedCount);

    // When smoothing is shown, threshold coloring applies to the smoothed line
    // (the raw line stays a neutral reference), matching LiefertermintreueTimeline.
    // At Median (no smoothing), coloring applies to the raw line as before.
    const coloredValues = showSmoothing ? smoothedValues : rawValues;
    const points = coloredValues.map((v) => {
      const zone = classifyThresholdValue(v, thresholdConfig);
      return zone ? { value: v, itemStyle: { color: ZONE_COLORS[zone] } } : v;
    });

    const series = showSmoothing
      ? [
          {
            name: "Roh",
            type: "line",
            symbol: "none",
            lineStyle: { color: "#d1d5db", width: 1.5, type: "dashed" },
            itemStyle: { color: "#d1d5db" },
            data: rawValues,
          },
          {
            name: `Geglättet (${rollingWindowDays}d)`,
            type: "line",
            symbol: "circle",
            symbolSize: 5,
            smooth: 0.2,
            lineStyle: { color: "#EF4444", width: 2.5 },
            itemStyle: { color: "#EF4444" },
            data: points,
            emphasis: { focus: "series" },
          },
        ]
      : [
          {
            name: `${AGGREGATION_LABELS[aggregation]} Verzug (Tage)`,
            type: "line",
            symbol: "circle",
            symbolSize: 5,
            smooth: 0.2,
            lineStyle: { color: "#EF4444", width: 2.5 },
            itemStyle: { color: "#EF4444" },
            data: points,
            emphasis: { focus: "series" },
          },
        ];

    return {
      backgroundColor: "transparent",
      grid: { top: 16, right: 16, bottom: 52, left: 16, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        textStyle: { fontSize: 12 },
        formatter: (params) => {
          const idx = params[0]?.dataIndex ?? 0;
          const date = dates[idx] ?? "";
          const late = lateCounts[idx] ?? 0;
          const elig = eligibleCounts[idx] ?? 0;
          const point = sampledPoints[idx];
          const lines = [`<b>${formatXLabel(date, granularity)}</b>`];
          if (showSmoothing) {
            lines.push(`Roher Wert: <b>${fmtDays(rawValues[idx])}</b>`);
            lines.push(`Geglättet (${rollingWindowDays}d): <b>${fmtDays(smoothedValues[idx])}</b>`);
          } else {
            lines.push(`${AGGREGATION_LABELS[aggregation]} Verzug: <b>${fmtDays(rawValues[idx])}</b>`);
          }
          lines.push(`Verspätete Aufträge: <b>${late}</b>`, `Bewertete Aufträge: <b>${elig}</b>`);
          if (point?.openGroup) lines.push(fmtGroupBreakdown("offene Aufträge", point.openGroup));
          if (point?.completedGroup) {
            const label = population === "all" ? "abgeschlossene Aufträge" : "verspätet abgeschlossene Aufträge";
            lines.push(fmtGroupBreakdown(label, point.completedGroup));
          }
          return lines.join("<br/>");
        },
      },
      xAxis: {
        type: "category",
        data: dates,
        boundaryGap: false,
        axisLabel: {
          fontSize: 12,
          color: "#6b7280",
          hideOverlap: true,
          formatter: (val) => formatXLabel(val, granularity),
          rotate: granularity === "day" && dates.length > 30 ? 30 : 0,
        },
        axisLine: { lineStyle: { color: "#e5e7eb" } },
        axisTick: { lineStyle: { color: "#e5e7eb" } },
      },
      yAxis: {
        type: "value",
        min: 0,
        axisLabel: { fontSize: 12, color: "#6b7280", formatter: (v) => `${v}d` },
        splitLine: { lineStyle: { color: "#f3f4f6", type: "dashed" } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series,
      dataZoom: [
        { type: "inside", start: 0, end: 100, zoomOnMouseWheel: true, moveOnMouseMove: true },
        {
          type: "slider", bottom: 4, height: 18, start: 0, end: 100,
          borderColor: "transparent", backgroundColor: "#f3f4f6",
          fillerColor: "rgba(239, 68, 68, 0.15)",
          handleStyle: { color: "#EF4444", borderColor: "#EF4444" },
          textStyle: { fontSize: 10, color: "#9ca3af" }, showDetail: false,
        },
      ],
      ...(showSmoothing ? { legend: { top: 0, right: 0, textStyle: { fontSize: 11 } } } : {}),
    };
  }, [sampledPoints, rollingSampled, showSmoothing, rollingWindowDays, granularity, aggregation, population, thresholdConfig]);

  const hasData = !!(sampledPoints?.length > 0);
  const displayTitle = componentTitle || DEFAULT_TITLE;
  const latestValue = showSmoothing
    ? (rollingSampled.length ? rollingSampled[rollingSampled.length - 1][aggregation] : null)
    : (sampledPoints.length ? sampledPoints[sampledPoints.length - 1][aggregation] : null);

  return (
    <>
      <BaseTile title={displayTitle} loading={loading} error={error} minHeight="lg" className="h-full" padding="sm">
        <TileContent layout="flex" direction="column" gap="sm" grow className="-mt-2">
          {hasConfig && (
            <Tooltip.Provider delayDuration={150}>
              <DateScopeControl
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                granularity={granularity}
                onGranularityChange={setGranularity}
                dateRangeStart={dateRangeStart}
                onDateRangeStartChange={setDateRangeStart}
                dateRangeEnd={dateRangeEnd}
                onDateRangeEndChange={setDateRangeEnd}
                onApplyDateRange={(start, end) => { setDateRangeStart(start); setDateRangeEnd(end); }}
                loading={loading}
              />
              <div className="flex items-center justify-end px-1">
                <InfoIcon label={displayTitle} description={ABSOLUTE_DELAY_TIMELINE_INFO_TEXT} />
              </div>
            </Tooltip.Provider>
          )}

          {!hasConfig && !loading && (
            <TileStatusContent
              status="info"
              title="Einstellungen konfigurieren"
              message="Bitte Objekttyp, Soll-Termin und Ist-Termin in den Settings auswählen."
            />
          )}

          {hasConfig && !loading && !error && chartData && !hasData && (
            <TileStatusContent
              status="empty"
              title="Keine Daten"
              message="Für die gewählte Konfiguration wurden keine Daten gefunden."
            />
          )}

          {hasConfig && !loading && !error && hasData && (
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              <div className="flex flex-wrap gap-4 text-xs text-gray-600 px-1">
                <span>
                  Aktuell ({showSmoothing ? `geglättet, ${AGGREGATION_LABELS[aggregation]}` : AGGREGATION_LABELS[aggregation]}):{" "}
                  <strong className="text-gray-900">{fmtDays(latestValue)}</strong>
                </span>
                {chartData?.objects_excluded_missing_ref_date > 0 && (
                  <span className="text-gray-400">
                    {chartData.objects_excluded_missing_ref_date} ohne Soll-Termin (ausgeschlossen)
                  </span>
                )}
                {chartData?.objects_with_unparseable_ref_date > 0 && (
                  <span className="text-red-600" title="Der Soll-Termin-Wert konnte nicht als Datum erkannt werden — vermutlich ein Formatproblem (z.B. ungewöhnliches Datumsformat).">
                    {chartData.objects_with_unparseable_ref_date} Soll-Termine nicht erkannt (Formatproblem?)
                  </span>
                )}
                {chartData?.objects_with_unparseable_actual_date > 0 && (
                  <span className="text-red-600" title="Der Ist-Termin-Wert konnte nicht als Datum erkannt werden — vermutlich ein Formatproblem (z.B. ungewöhnliches Datumsformat).">
                    {chartData.objects_with_unparseable_actual_date} Ist-Termine nicht erkannt (Formatproblem?)
                  </span>
                )}
              </div>
              <div className="flex-1 min-h-0">
                <ReactECharts option={echartsOption} style={{ height: "100%", width: "100%", minHeight: 200 }} notMerge lazyUpdate />
              </div>
            </div>
          )}
        </TileContent>
      </BaseTile>

      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={(val) => {
          setIsSettingsModalOpen(val);
          if (!val) cancelDraftFilters();
        }}
        title="Absoluter Verzug über Zeit – Settings"
        getInitialValues={() => ({
          componentTitle, objectType, refDateType, refDateValue, refEventAttributeSource,
          actualDateType, actualDateValue, actualEventAttributeSources,
          tileEventAttributeFilter, aggregation, includeMode, rollingWindowDays,
          thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper,
        })}
        onApply={(vals) => {
          setComponentTitle(vals.componentTitle);
          setObjectType(vals.objectType);
          setRefDateType(vals.refDateType);
          setRefDateValue(vals.refDateValue);
          setRefEventAttributeSource(vals.refEventAttributeSource);
          setActualDateType(vals.actualDateType);
          setActualDateValue(vals.actualDateValue);
          setActualEventAttributeSources(vals.actualEventAttributeSources);
          setTileEventAttributeFilter(vals.tileEventAttributeFilter);
          setAggregation(vals.aggregation);
          setIncludeMode(vals.includeMode);
          setRollingWindowDays(vals.rollingWindowDays);
          setThresholdType(vals.thresholdType);
          setThresholdTarget(vals.thresholdTarget);
          setThresholdWarningPercentage(vals.thresholdWarningPercentage);
          setThresholdLower(vals.thresholdLower);
          setThresholdUpper(vals.thresholdUpper);
          applyDraftFilters();
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={(vals) => {
          if (!vals.objectType) return true;
          const refOk = vals.refDateType === "event_attribute"
            ? !!(vals.refEventAttributeSource?.activity && vals.refEventAttributeSource?.attribute)
            : !!vals.refDateValue;
          const actualOk = vals.actualDateType === "event_attribute"
            ? (vals.actualEventAttributeSources?.length > 0)
            : (vals.actualDateValue?.length > 0);
          return !refOk || !actualOk;
        }}
      >
        {({ values, setField }) => (
          <AbsoluteDelayTimelineSettings
            activeFile={activeFile}
            componentTitle={values.componentTitle}
            onComponentTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => {
              setField("objectType", v);
              setField("refDateValue", "");
              setField("refEventAttributeSource", null);
              setField("actualDateValue", []);
              setField("actualEventAttributeSources", []);
              setSettingsObjectType(v);
            }}
            objectTypes={objectTypes}
            refDateType={values.refDateType}
            onRefDateTypeChange={(v) => { setField("refDateType", v); setField("refDateValue", ""); setField("refEventAttributeSource", null); }}
            refDateValue={values.refDateValue}
            onRefDateValueChange={(v) => setField("refDateValue", v)}
            refEventAttributeSource={values.refEventAttributeSource}
            onRefEventAttributeSourceChange={(v) => setField("refEventAttributeSource", v)}
            actualDateType={values.actualDateType}
            onActualDateTypeChange={(v) => { setField("actualDateType", v); setField("actualDateValue", []); setField("actualEventAttributeSources", []); }}
            actualDateValue={values.actualDateValue}
            onActualDateValueChange={(v) => setField("actualDateValue", v)}
            actualEventAttributeSources={values.actualEventAttributeSources}
            onActualEventAttributeSourcesChange={(v) => setField("actualEventAttributeSources", v)}
            getAttributesForActivity={getAttributesForActivity}
            activitiesForType={activitiesForType}
            dateAttrsForType={dateAttrsForType}
            activitiesLoading={activitiesLoading}
            dateAttrsLoading={dateAttrsLoading}
            tileEventAttributeFilter={values.tileEventAttributeFilter}
            onTileEventAttributeFilterChange={(v) => setField("tileEventAttributeFilter", v)}
            aggregation={values.aggregation}
            onAggregationChange={(v) => setField("aggregation", v)}
            includeMode={values.includeMode}
            onIncludeModeChange={(v) => setField("includeMode", v)}
            rollingWindowDays={values.rollingWindowDays}
            onRollingWindowDaysChange={(v) => setField("rollingWindowDays", v)}
            thresholdType={values.thresholdType}
            onThresholdTypeChange={(v) => setField("thresholdType", v)}
            thresholdTarget={values.thresholdTarget}
            onThresholdTargetChange={(v) => setField("thresholdTarget", v)}
            thresholdWarningPercentage={values.thresholdWarningPercentage}
            onThresholdWarningPercentageChange={(v) => setField("thresholdWarningPercentage", v)}
            thresholdLower={values.thresholdLower}
            onThresholdLowerChange={(v) => setField("thresholdLower", v)}
            thresholdUpper={values.thresholdUpper}
            onThresholdUpperChange={(v) => setField("thresholdUpper", v)}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
          />
        )}
      </TileSettingsModal>
    </>
  );
};

export default AbsoluteDelayTimeline;
